/**
 * The rules themselves — the data half of `pricing-rules.ts`.
 *
 * Split off for the reason `seed/offering-data.ts` and
 * `seed/shoot-pricing-data.ts` are: a list that grows a row per decision does
 * not belong in the file that parses and validates it.
 *
 * ── EVERY DEFAULT IS WHAT THE CODE ALREADY CHARGED ───────────────────────
 * Each `defaultValue` below is copied from the `const` that used to hold it,
 * and the constant is still exported from its own module as that default. So
 * wiring a rule through here changes nothing anybody is quoted until somebody
 * types a different number in the panel — which is the whole point, and what
 * the parity tests in `tests/pricing-rules.test.ts` pin.
 *
 * Bounds are guard rails, not opinions: they stop a typo (9 900 % instead of
 * 99 %) and nothing else. Where a rule has a real business ceiling — the
 * discount cap that may never reach 100 % — the bound states it.
 */

import type { PricingRule } from './pricing-rules.ts'
import { DEFAULT_TAX_RATE_BPS } from './schema/columns.ts'
import { DEFAULT_TRAVEL_RATE_NET_MINOR, TRAVEL_RATE_KEY } from './travel.ts'

const PERCENT = 100

export const PRICING_RULES: readonly PricingRule[] = [
  /* ── ZWROTY I ANULOWANIE (spec §10 · umowa punkt 20) ──────────────────── */
  {
    key: 'refund.full_bps',
    group: 'zwroty',
    label: 'Zwrot przy odwołaniu z wyprzedzeniem',
    hint: 'Ile wraca do klienta, gdy odwołuje wcześniej niż 3 dni przed terminem.',
    unit: 'BPS',
    defaultValue: 100 * PERCENT,
    min: 0,
    max: 100 * PERCENT,
    editable: true,
  },
  {
    key: 'refund.half_bps',
    group: 'zwroty',
    label: 'Zwrot przy odwołaniu na krótko przed',
    hint: 'Ile wraca, gdy odwołuje od 3 dni do 24 godzin przed terminem.',
    unit: 'BPS',
    defaultValue: 50 * PERCENT,
    min: 0,
    max: 100 * PERCENT,
    editable: true,
  },
  {
    key: 'refund.none_bps',
    group: 'zwroty',
    label: 'Zwrot przy odwołaniu w ostatniej chwili',
    hint: 'Ile wraca, gdy odwołuje później niż 24 godziny przed terminem albo się nie stawia.',
    unit: 'BPS',
    defaultValue: 0,
    min: 0,
    max: 100 * PERCENT,
    editable: true,
  },
  {
    key: 'refund.performer_share_bps',
    group: 'zwroty',
    label: 'Udział wykonawcy w zatrzymanej kwocie',
    hint: 'Człowiek, który trzymał termin, dostaje tę część tego, co zostaje w FLOU. Połowa daje dzisiejsze 0/25/50%.',
    unit: 'BPS',
    defaultValue: 50 * PERCENT,
    min: 0,
    max: 100 * PERCENT,
    editable: true,
  },

  /*
   * ── KIEDY, a nie ILE ────────────────────────────────────────────────
   * Cztery liczby, które decydują, KTÓRY z trzech pasów wyżej obowiązuje i
   * ile razy wolno przenieść termin. Przyszły z drugiego, równolegle
   * zbudowanego rejestru reguł, którego ekran przepadł przy scalaniu — a
   * `readRefundLimits` czytał je i czyta dalej, więc bez nich właściciel
   * zmieniał stawki zwrotu, nie widząc progów, od których zależą.
   *
   * Klucze zostają z przedrostkiem `pricing.`, niezgodnie z resztą tego
   * pliku, bo pod `pricing.refund.full_days` w `site_setting` LEŻY JUŻ
   * zapisana wartość. Przemianowanie osierociłoby ją po cichu; niezgodny
   * przedrostek widać od razu.
   */
  {
    key: 'pricing.refund.full_days',
    group: 'zwroty',
    label: 'Pełny zwrot do (dni przed)',
    hint: 'Odwołanie wcześniej niż tyle dni przed terminem dostaje pas „z wyprzedzeniem".',
    unit: 'DAYS',
    defaultValue: 3,
    min: 0,
    max: 90,
    editable: true,
  },
  {
    key: 'pricing.refund.none_hours',
    group: 'zwroty',
    label: 'Bez zwrotu od (godzin przed)',
    hint: 'Odwołanie później niż tyle godzin przed terminem dostaje pas „w ostatniej chwili".',
    unit: 'HOURS',
    defaultValue: 24,
    min: 0,
    max: 336,
    editable: true,
  },
  {
    key: 'pricing.reschedule.max',
    group: 'zwroty',
    label: 'Ile razy można przenieść termin',
    hint: 'Przeniesienie z powodu pogody nie zużywa tego limitu.',
    unit: 'COUNT',
    defaultValue: 1,
    min: 0,
    max: 10,
    editable: true,
  },
  {
    key: 'pricing.reschedule.min_hours',
    group: 'zwroty',
    label: 'Przeniesienie najpóźniej (godzin przed)',
    hint: 'Bliżej terminu panel nie pozwoli już przenieść rezerwacji.',
    unit: 'HOURS',
    defaultValue: 24,
    min: 0,
    max: 336,
    editable: true,
  },

  /* ── ZMIANY W ZLECENIU (spec §6, §8 · umowa punkty 8 i 9) ─────────────── */
  {
    key: 'revision.extra_max_bps',
    group: 'zlecenie',
    label: 'Górna granica dopłaty za poprawkę ponad limit',
    hint: 'Liczona od ceny JEDNEJ sztuki, za każdą poprawianą sztukę. Wyżej wykonawca nie wyceni.',
    unit: 'BPS',
    defaultValue: 30 * PERCENT,
    min: 0,
    max: 100 * PERCENT,
    editable: true,
  },
  /* ── DZIEŃ NAGRANIOWY: CO DOCHODZI DO PLANU (właściciel, 2026-10-12) ──
     Zestaw operatora obejmuje 2 mikroporty, statywy, 2 światła i światło RGB.
     Te dwie reguły wyceniają jedyne dwie rzeczy, które można dołożyć.

     Jako reguły, a nie stałe w komponencie, z tego samego powodu co dopłata
     za surówkę pod spodem: to są CENY, a ceny edytuje panel. Limity, które
     opisują zawartość zestawu — ile mikroportów jest w cenie, ile świateł —
     zostają w kodzie, bo opisują sprzęt, nie cennik. */
  {
    key: 'operator.extra_mic_pair_net_minor',
    group: 'zlecenie',
    label: 'Dopłata za dodatkową parę mikroportów',
    hint: 'W zestawie są 2 mikroporty. Klient wybiera, ile osób mówi do kamery jednocześnie — każda kolejna para to ta kwota.',
    unit: 'MINOR',
    defaultValue: 6900,
    min: 0,
    max: 100_00,
    editable: true,
  },
  {
    key: 'operator.extra_lights_net_minor',
    group: 'zlecenie',
    label: 'Dopłata za dodatkowy zestaw dwóch świateł',
    hint: 'W cenie są 2 światła (do jednej kamery) i światło RGB. To kwota za drugi komplet.',
    unit: 'MINOR',
    defaultValue: 12900,
    min: 0,
    max: 200_00,
    editable: true,
  },
  {
    key: 'footage.overage_net_minor_per_minute',
    group: 'zlecenie',
    label: 'Dopłata za minutę surówki ponad limit',
    hint: 'Doliczana z góry przy składaniu zamówienia, nie po fakcie.',
    unit: 'MINOR',
    defaultValue: 200,
    min: 0,
    max: 100_00,
    editable: true,
  },

  /* ── KOSZYK, KOORDYNATOR I RABATY (spec §13, §14, §12) ────────────────── */
  {
    key: 'coordinator.fee_bps',
    group: 'koszyk',
    label: 'Opłata za koordynatora',
    hint: 'Liczona tylko od pozycji od sztuki. Rezerwacje studia i sprzętu nie wchodzą do podstawy.',
    unit: 'BPS',
    defaultValue: 12 * PERCENT,
    min: 0,
    max: 100 * PERCENT,
    editable: true,
  },
  {
    key: 'coordinator.min_fee_net_minor',
    group: 'koszyk',
    label: 'Najniższa opłata za koordynatora',
    hint: 'Podłoga dla powyższego procentu — przy małym zamówieniu liczy się ta kwota.',
    unit: 'MINOR',
    defaultValue: 39_00,
    min: 0,
    max: 10_000_00,
    editable: true,
  },
  {
    key: 'codes.max_discount_bps',
    group: 'koszyk',
    label: 'Najwyższy rabat, jaki może zabrać kod',
    hint: 'Dotyczy KWOTY zabranej z koszyka, nie procentu wpisanego na kodzie. Koszyk nigdy nie schodzi do zera, bo zera nie da się opłacić.',
    unit: 'BPS',
    defaultValue: 99 * PERCENT,
    min: 0,
    // Deliberately below 100 %: a zeroed basket is an order nobody can pay,
    // which the payment-window job then kills as FAILED. See `codes.ts`.
    max: 99 * PERCENT,
    editable: true,
  },
  {
    key: 'consultation.bonus_net_minor',
    group: 'koszyk',
    label: 'Ile spotkanie strategiczne wraca kodem',
    hint: 'Kod wystawiany po odbytym spotkaniu, do wydania na kolejne zlecenie. Domyślnie tyle, ile spotkanie kosztuje — cenę samego spotkania ustawiasz na jego pozycji w katalogu.',
    unit: 'MINOR',
    defaultValue: 199_00,
    min: 0,
    max: 100_000_00,
    editable: true,
  },
  /*
     PAKIET 10 ROLEK. Owner, 2026-10-15: „dodajemy pakiety 10 sztuk z opcją
     przełącznika na stronie" — ten sam poziom, ok. −10% od dziesięciu
     pojedynczych. Trzy kwoty, nie jeden procent: 889 / 2 239 / 4 489 zł to
     ceny podane wprost, a procent zaokrąglałby je do groszy, których nikt nie
     ustalał. Montażysta dostaje swoją zwykłą stawkę za każdą rolkę.
   */
  {
    key: 'reels.pack10.clean_net_minor',
    group: 'koszyk',
    label: 'Pakiet 10 rolek Clean',
    hint: 'Cena za dziesięć rolek Clean kupionych razem. Pojedyncza rolka kosztuje tyle, ile na jej pozycji w katalogu.',
    unit: 'MINOR',
    defaultValue: 889_00,
    min: 1_00,
    max: 100_000_00,
    editable: true,
  },
  {
    key: 'reels.pack10.dynamic_net_minor',
    group: 'koszyk',
    label: 'Pakiet 10 rolek Dynamic',
    hint: 'Cena za dziesięć rolek Dynamic kupionych razem.',
    unit: 'MINOR',
    defaultValue: 2_239_00,
    min: 1_00,
    max: 100_000_00,
    editable: true,
  },
  {
    key: 'reels.pack10.advanced_net_minor',
    group: 'koszyk',
    label: 'Pakiet 10 rolek Advanced',
    hint: 'Cena za dziesięć rolek Advanced kupionych razem.',
    unit: 'MINOR',
    defaultValue: 4_489_00,
    min: 1_00,
    max: 100_000_00,
    editable: true,
  },

  /* ── SPRZĘT I DOJAZD (spec §11, §12) ──────────────────────────────────── */
  {
    key: 'rental.late_fee_multiplier',
    group: 'sprzet',
    label: 'Krotność stawki dziennej za dzień zwłoki',
    hint: 'Kara za sprzęt oddany po terminie. Dwa, bo spóźniona kamera to nie jeden stracony dzień, tylko dzień plus wynajem, którego nie da się zrealizować.',
    unit: 'FACTOR',
    defaultValue: 2,
    min: 1,
    max: 10,
    editable: true,
  },
  {
    key: TRAVEL_RATE_KEY,
    group: 'sprzet',
    label: 'Dojazd poza miasto fotografa, za kilometr',
    hint: 'W obrębie miasta fotografa bezpłatnie. Ta stawka była edytowalna jako pierwsza — reszta ekranu dołączyła do niej.',
    unit: 'MINOR',
    // `travel.ts` keeps both the key and the default, because the seed and the
    // shoot-day arithmetic already read them from there.
    defaultValue: DEFAULT_TRAVEL_RATE_NET_MINOR,
    min: 0,
    max: 100_00,
    editable: true,
  },

  /* ── NARZUCONE Z ZEWNĄTRZ ─────────────────────────────────────────────── */
  {
    key: 'tax.rate_bps',
    group: 'ustawowe',
    label: 'VAT na usługi twórcze',
    hint: 'Doliczany do każdej kwoty netto na tym ekranie.',
    unit: 'BPS',
    // The column default, not a copy of it — see `schema/columns.ts`.
    defaultValue: DEFAULT_TAX_RATE_BPS,
    min: 0,
    max: 100 * PERCENT,
    editable: false,
    lockedBecause:
      'Stawkę VAT ustala ustawa, nie FLOU. Zmiana tutaj rozjechałaby się z fakturami już wystawionymi przy 23% — jeśli ustawodawca ją ruszy, poprawiamy to wdrożeniem razem z serią faktur.',
  },
  {
    key: 'withdrawal.days',
    group: 'ustawowe',
    label: 'Termin odstąpienia od umowy',
    hint: 'Liczony od zawarcia umowy. Dotyczy konsumenta i przedsiębiorcy na prawach konsumenta.',
    unit: 'DAYS',
    defaultValue: 14,
    min: 14,
    max: 365,
    editable: false,
    lockedBecause:
      'Czternaście dni daje ustawa o prawach konsumenta. Krótszy termin jest nieważny z mocy prawa, a dłuższy trzeba najpierw zapisać w regulaminie — wtedy zmieniamy oba naraz.',
  },
]
